import {
  ArrowDown,
  ArrowRight,
  Check,
  FileOutput,
  FileWarning,
  FolderUp,
  LockKeyhole,
  ScanSearch,
  ShieldCheck,
  UserRound,
  Wrench,
} from 'lucide-react';
import { useRef, useState } from 'react';
import { Button } from '../../design-system/Button';
import { SUPPORTED_FILE_ACCEPT, unsupportedFileMessage } from '../../product/capabilities';

interface WelcomePageProps {
  onFile: (file: File) => void;
  onOpenWorkspace: () => void;
}

const trustFacts = [
  { icon: LockKeyhole, title: 'No upload', detail: 'Files never leave your device.' },
  { icon: UserRound, title: 'No account', detail: 'Use privately without signing in.' },
  { icon: ShieldCheck, title: 'Original untouched', detail: 'We work on copies. Your file stays safe.' },
] as const;

const recoverySteps = [
  { icon: ScanSearch, title: 'Analyze', detail: 'Inspect structure and content on your device.' },
  { icon: Wrench, title: 'Repair when safe', detail: 'Apply conservative repairs only when it’s safe to do so.' },
  { icon: Check, title: 'Validate output', detail: 'Verify structure and readability before saving.' },
] as const;

const carePoints = [
  {
    icon: LockKeyhole,
    title: 'Integrity before repair',
    detail: 'We analyze structure and content first, then repair only when it is safe while preserving the original.',
  },
  {
    icon: FileOutput,
    title: 'Recover what remains',
    detail: 'When full repair is not possible, content rescue can preserve the readable material that still matters.',
  },
  {
    icon: ShieldCheck,
    title: 'Validate every output',
    detail: 'Recovery outputs are reopened and checked before MendByte offers them for download.',
  },
] as const;

export function WelcomePage({ onFile, onOpenWorkspace }: WelcomePageProps): React.JSX.Element {
  const inputRef = useRef<HTMLInputElement>(null);
  const [selectionError, setSelectionError] = useState<string | null>(null);
  const selectFile = (files: FileList | null): void => {
    const selected = files?.item(0);
    if (!selected) return;
    const message = unsupportedFileMessage(selected.name);
    setSelectionError(message);
    if (!message) onFile(selected);
  };

  return (
    <div className="welcome-page">
      <section className="welcome-hero" aria-labelledby="welcome-title">
        <div className="welcome-hero__copy">
          <h1 id="welcome-title">Your file may <br />not be lost yet.</h1>
          <p className="welcome-hero__lead">Diagnose damaged documents privately. MendByte analyzes, repairs, and extracts supported files directly in your browser.</p>

          <div className="welcome-trust" aria-label="MendByte privacy facts">
            {trustFacts.map((fact) => {
              const Icon = fact.icon;
              return (
                <div key={fact.title}>
                  <Icon aria-hidden="true" />
                  <span><strong>{fact.title}</strong><small>{fact.detail}</small></span>
                </div>
              );
            })}
          </div>

          <div className="welcome-actions">
            <Button variant="primary" onClick={() => inputRef.current?.click()}><FolderUp aria-hidden="true" />Choose a file</Button>
            <Button variant="secondary" onClick={onOpenWorkspace}><FileOutput aria-hidden="true" />Open recovery workspace</Button>
            <input
              ref={inputRef}
              className="visually-hidden"
              type="file"
              aria-label="Choose a file to analyze"
              accept={SUPPORTED_FILE_ACCEPT}
              onChange={(event) => {
                selectFile(event.target.files);
                event.currentTarget.value = '';
              }}
            />
          </div>

          {selectionError ? <p className="welcome-file-error" role="alert">{selectionError}</p> : null}
          <p className="welcome-formats"><strong>Supported formats:</strong><span>XLSX · XLSM · DOCX · DOCM · PPTX · PPTM · ZIP · PDF · ODT</span></p>
        </div>

        <div className="recovery-path" aria-labelledby="recovery-path-title">
          <h2 id="recovery-path-title">A careful recovery path</h2>
          <div className="recovery-path__diagram">
            <div className="recovery-path__source" aria-hidden="true">
              <FileWarning />
            </div>
            <ArrowRight className="recovery-path__source-arrow" aria-hidden="true" />
            <ArrowDown className="recovery-path__source-arrow-mobile" aria-hidden="true" />
            <div className="recovery-path__steps">
              {recoverySteps.map((step, index) => {
                const Icon = step.icon;
                return (
                  <div className="recovery-step" key={step.title}>
                    <span className="recovery-step__icon"><Icon aria-hidden="true" /></span>
                    <span><strong>{step.title}</strong><small>{step.detail}</small></span>
                    {index < recoverySteps.length - 1 ? <ArrowRight className="recovery-step__arrow" aria-hidden="true" /> : null}
                  </div>
                );
              })}
            </div>
            <div className="recovery-path__output" aria-hidden="true"><FileOutput /><Check /></div>
            <div className="recovery-path__extract">
              <span className="recovery-step__icon"><ScanSearch aria-hidden="true" /></span>
              <span><strong>Extract content</strong><small>Extract readable content even if the file cannot be fully repaired.</small></span>
            </div>
          </div>
          <p className="recovery-path__privacy"><ShieldCheck aria-hidden="true" />All processing happens locally in your browser. Nothing is uploaded.</p>
        </div>
      </section>

      <section className="care-section" aria-labelledby="care-title">
        <h2 id="care-title">Built for careful recovery</h2>
        <div className="care-list">
          {carePoints.map((point) => {
            const Icon = point.icon;
            return (
              <article key={point.title}>
                <Icon aria-hidden="true" />
                <div><h3>{point.title}</h3><p>{point.detail}</p></div>
                <ArrowRight className="care-list__arrow" aria-hidden="true" />
              </article>
            );
          })}
        </div>
      </section>
    </div>
  );
}
